import { Avatar, Icon } from '@amelio/ui-library';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { jobAndDepartmentLabel } from '@/pages/Competencies/components/employees/personDisplay';
import { fullName, hasName, initials, MUTED } from '@/pages/CompetencyProfilesAdmin/helpers';
import { getTextByLanguage } from '@/utils/localization';
import { getProfilePictureUrl } from '@/utils/user';

type EmployeeRosterTileProps = {
    employee: RosterEmployee;
    onSelect: () => void;
};

// One roster tile: avatar + name + "job title · department" subtitle, plus a profile
// pastille (teal dot + profile name) or a "Sans profil" pastille (grey dot), and a
// chevron. The whole tile is a button that opens the per-employee flow.
export const EmployeeRosterTile = ({ employee, onSelect }: EmployeeRosterTileProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { getJobTitle, getDepartment } = useCompanyInformation();

    const profileName = employee.assignedProfileName
        ? getTextByLanguage(employee.assignedProfileName, language)
        : null;

    return (
        <button
            type={'button'}
            onClick={onSelect}
            className={
                'group ds-rounded-md bg-white-80 ds-p-2xl flex w-full items-start gap-2 border border-white text-left backdrop-blur-sm transition-all hover:shadow-lg sm:gap-6'
            }
            data-testid={`roster-employee-${employee.id}`}
        >
            <Avatar
                bgColor={'gradient'}
                src={getProfilePictureUrl(employee.profilePictureUrl) || undefined}
                initials={initials(employee)}
                alt={fullName(employee)}
                className={'!h-[56px] !w-[56px]'}
            />

            <div className={'min-w-0 flex-1'}>
                <p
                    className={
                        'group-hover:text-primary text-navy text-body-lg truncate font-semibold transition-colors'
                    }
                >
                    {hasName(employee)
                        ? fullName(employee)
                        : t('competencies.competencyProfilesAdmin.anonymous')}
                </p>

                <p className={'text-body-sm mt-2 mb-4 truncate text-gray-500'}>
                    {jobAndDepartmentLabel(employee, {
                        getJobTitle,
                        getDepartment,
                        fallback: t('competencies.competencyProfilesAdmin.noJobDepartment'),
                    })}
                </p>

                {profileName ? (
                    <div className={'flex items-center gap-[6px]'}>
                        <span className={'bg-primary h-[6px] w-[6px] shrink-0 rounded-full'} />

                        <span className={'text-primary text-caption truncate font-medium'}>
                            {profileName}
                        </span>
                    </div>
                ) : (
                    <div className={'flex items-center gap-[6px]'}>
                        <span
                            className={
                                'h-[6px] w-[6px] shrink-0 rounded-full bg-[hsl(215,16%,47%)]/50'
                            }
                        />

                        <span
                            className={'text-caption truncate font-medium'}
                            style={{ color: MUTED }}
                        >
                            {t('competencies.competencyProfilesAdmin.tile.noProfile')}
                        </span>
                    </div>
                )}
            </div>

            <Icon
                name={'arrow-arrow-right-1'}
                color={'inherit'}
                colorClassName={'text-navy/40 transition-colors group-hover:text-primary'}
            />
        </button>
    );
};
